import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@iptv/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { adminFetch, type ApiError } from '../lib/api';
import { useTotpChallenge } from '../lib/totp';
import { SortableTh, useTableSort } from '../lib/useTableSort';

interface BumpRow {
  id: number;
  code: string;
  name: string;
  description: string | null;
  price_cents: number;
  currency: string;
  applies_to_plan_codes: string[] | null;
  applies_to_site_slugs: string[] | null;
  default_checked: boolean;
  sort_order: number;
  active: boolean;
}

interface BumpForm {
  code: string;
  name: string;
  description: string;
  price_cents: number;
  currency: string;
  applies_to_plan_codes: string;
  applies_to_site_slugs: string;
  default_checked: boolean;
  sort_order: number;
}

const EMPTY_FORM: BumpForm = {
  code: '',
  name: '',
  description: '',
  price_cents: 999,
  currency: 'USD',
  applies_to_plan_codes: '',
  applies_to_site_slugs: '',
  default_checked: false,
  sort_order: 0,
};

function bumpToForm(b: BumpRow): BumpForm {
  return {
    code: b.code,
    name: b.name,
    description: b.description ?? '',
    price_cents: b.price_cents,
    currency: b.currency,
    applies_to_plan_codes: (b.applies_to_plan_codes ?? []).join(', '),
    applies_to_site_slugs: (b.applies_to_site_slugs ?? []).join(', '),
    default_checked: b.default_checked,
    sort_order: b.sort_order,
  };
}

function splitCsv(s: string): string[] | null {
  const parts = s
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);
  return parts.length > 0 ? parts : null;
}

function formToPayload(f: BumpForm): Record<string, unknown> {
  return {
    code: f.code.trim(),
    name: f.name.trim(),
    description: f.description.trim() || null,
    price_cents: Number(f.price_cents),
    currency: f.currency.trim().toUpperCase() || 'USD',
    applies_to_plan_codes: splitCsv(f.applies_to_plan_codes),
    applies_to_site_slugs: splitCsv(f.applies_to_site_slugs),
    default_checked: f.default_checked,
    sort_order: Number(f.sort_order),
  };
}

export function BumpsPage() {
  const queryClient = useQueryClient();
  const challengeTotp = useTotpChallenge();
  const [editing, setEditing] = useState<BumpRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<BumpForm>(EMPTY_FORM);

  const bumps = useQuery<BumpRow[]>({
    queryKey: ['admin', 'bumps'],
    queryFn: () => adminFetch<BumpRow[]>('/api/v1/admin/bumps'),
  });
  const { sorted, sortKey, sortDir, toggleSort } = useTableSort<BumpRow>(bumps.data, {
    initialKey: 'sort_order',
    initialDir: 'asc',
  });

  const create = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      adminFetch<BumpRow>('/api/v1/admin/bumps', { method: 'POST', body: payload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'bumps'] });
      setCreating(false);
      setForm(EMPTY_FORM);
    },
  });

  const update = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: Record<string, unknown> }) =>
      adminFetch<BumpRow>(`/api/v1/admin/bumps/${id}`, { method: 'PATCH', body: payload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'bumps'] });
      setEditing(null);
    },
  });

  const remove = useMutation({
    mutationFn: ({ id, token }: { id: number; token: string }) =>
      adminFetch(`/api/v1/admin/bumps/${id}?totp_challenge_token=${encodeURIComponent(token)}`, {
        method: 'DELETE',
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'bumps'] }),
  });

  function openCreate() {
    setForm(EMPTY_FORM);
    setCreating(true);
  }
  function openEdit(b: BumpRow) {
    setForm(bumpToForm(b));
    setEditing(b);
  }
  async function handleDelete(b: BumpRow) {
    const token = await challengeTotp({
      message: `Delete bump "${b.name}" (${b.code}). This cannot be undone from the admin UI.`,
    });
    if (!token) return;
    try {
      await remove.mutateAsync({ id: b.id, token });
    } catch (err) {
      alert((err as ApiError).detail ?? 'Delete failed');
    }
  }
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const payload = formToPayload(form);
    if (editing) await update.mutateAsync({ id: editing.id, payload });
    else await create.mutateAsync(payload);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end">
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4" /> New bump
        </Button>
      </div>
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50">
            <tr>
              <SortableTh sortKey="code" current={sortKey} dir={sortDir} onSort={toggleSort}>
                Code
              </SortableTh>
              <SortableTh sortKey="name" current={sortKey} dir={sortDir} onSort={toggleSort}>
                Name
              </SortableTh>
              <SortableTh sortKey="price_cents" current={sortKey} dir={sortDir} onSort={toggleSort}>
                Price
              </SortableTh>
              <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                Sites
              </th>
              <SortableTh
                sortKey="default_checked"
                current={sortKey}
                dir={sortDir}
                onSort={toggleSort}
              >
                Default
              </SortableTh>
              <SortableTh sortKey="active" current={sortKey} dir={sortDir} onSort={toggleSort}>
                Active
              </SortableTh>
              <th className="px-3 py-2 text-right text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {bumps.isPending && (
              <tr>
                <td colSpan={7} className="px-3 py-4 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            )}
            {bumps.data?.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-4 text-center text-muted-foreground">
                  No bumps yet.
                </td>
              </tr>
            )}
            {sorted.map((b) => (
              <tr key={b.id} className="border-t">
                <td className="px-3 py-2 font-mono text-xs">{b.code}</td>
                <td className="px-3 py-2">{b.name}</td>
                <td className="px-3 py-2">${(b.price_cents / 100).toFixed(2)}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground">
                  {b.applies_to_site_slugs?.join(', ') || 'all'}
                </td>
                <td className="px-3 py-2">{b.default_checked ? 'checked' : '—'}</td>
                <td className="px-3 py-2">
                  {b.active ? (
                    <Badge variant="success">yes</Badge>
                  ) : (
                    <Badge variant="secondary">no</Badge>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(b)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(b)}
                      disabled={remove.isPending}
                      title={
                        b.active
                          ? 'Soft-delete this bump'
                          : 'Already deleted — click to delete again'
                      }
                    >
                      <Trash2
                        className={`h-3.5 w-3.5 ${b.active ? '' : 'text-muted-foreground'}`}
                      />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog
        open={creating || editing !== null}
        onOpenChange={(open) => {
          if (!open) {
            setCreating(false);
            setEditing(null);
          }
        }}
      >
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${editing.name}` : 'New bump'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="code">Code</Label>
              <Input
                id="code"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                disabled={editing !== null}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="price_cents">Price (cents)</Label>
              <Input
                id="price_cents"
                type="number"
                min={0}
                value={form.price_cents}
                onChange={(e) => setForm({ ...form, price_cents: Number(e.target.value) })}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="currency">Currency</Label>
              <Input
                id="currency"
                value={form.currency}
                onChange={(e) => setForm({ ...form, currency: e.target.value })}
                maxLength={3}
              />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label htmlFor="sites">Applies to site slugs (comma sep, empty = all)</Label>
              <Input
                id="sites"
                value={form.applies_to_site_slugs}
                placeholder="wc-en, wc-es"
                onChange={(e) => setForm({ ...form, applies_to_site_slugs: e.target.value })}
              />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label htmlFor="plans">Applies to plan codes (comma sep, empty = all)</Label>
              <Input
                id="plans"
                value={form.applies_to_plan_codes}
                placeholder="wc-pass-60d-2c, year-1y-5c"
                onChange={(e) => setForm({ ...form, applies_to_plan_codes: e.target.value })}
              />
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Checkbox
                id="default_checked"
                checked={form.default_checked}
                onCheckedChange={(c) => setForm({ ...form, default_checked: c === true })}
              />
              <Label htmlFor="default_checked" className="cursor-pointer">
                Default checked at checkout
              </Label>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sort_order">Sort order</Label>
              <Input
                id="sort_order"
                type="number"
                value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })}
              />
            </div>
            {(create.isError || update.isError) && (
              <div className="md:col-span-2 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {String(
                  (create.error as ApiError | null)?.detail ??
                    (update.error as ApiError | null)?.detail ??
                    'Something went wrong',
                )}
              </div>
            )}
            <div className="md:col-span-2 flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setCreating(false);
                  setEditing(null);
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={create.isPending || update.isPending}>
                {create.isPending || update.isPending
                  ? 'Saving…'
                  : editing
                    ? 'Save changes'
                    : 'Create bump'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
